import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { type Client } from "@/data/adminData"
import { adminActivityApi, adminSystemApi, type ActivityEventDTO } from "@/lib/api"

const statusColor: Record<string, string> = {
  done:           'oklch(0.60 0.15 148)',
  processing:     'oklch(0.52 0.16 262)',
  queued:         'oklch(0.52 0.14 197)',
  error:          'oklch(0.58 0.17 28)',
  paused:         'oklch(0.65 0.15 58)',
  not_started:    'oklch(0.70 0 0)',
  not_configured: 'oklch(0.58 0.17 50)',
  cancelled:      'oklch(0.70 0 0)',
}


export function AdminDashboard({ clients }: { clients: Client[] }) {
  const active = clients.filter(c => c.status === 'active').length
  const totalStorage = clients.reduce((s, c) => s + c.storage.used, 0).toFixed(1)
  const totalCredits = clients.reduce((s, c) => s + c.credits.used, 0)

  const [activity, setActivity] = useState<ActivityEventDTO[]>([])
  const [aiCost, setAiCost] = useState(0)
  useEffect(() => {
    adminActivityApi.list().then(setActivity).catch(() => {})
    adminSystemApi.getUsage().then(rows => setAiCost(rows.reduce((acc, r) => acc + r.cost_eur, 0))).catch(() => {})
  }, [])

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Dashboard</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Overview of TwinModel platform</p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {[
          { label:'Total Clients',     value: clients.length,             sub:`${active} active`              },
          { label:'Storage Used',      value:`${totalStorage} GB`,        sub:'across all accounts'           },
          { label:'API Credits Used',  value: totalCredits.toLocaleString(), sub:'this month (shared)'        },
          { label:'AI Cost',           value:`€${aiCost.toFixed(2)}`,     sub:'vision + generation, all time' },
        ].map(({ label, value, sub }) => (
          <Card key={label}>
            <CardHeader className="pb-1">
              <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold tracking-tight">{value}</div>
              <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-4 border-b text-sm font-semibold">Recent CLI Activity</div>
        {activity.length === 0 ? (
          <div className="px-5 py-8 text-sm text-muted-foreground text-center">No jobs yet</div>
        ) : activity.map(ev => (
          <div key={ev.id} className="flex items-start gap-3 px-5 py-3 border-b last:border-0">
            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: statusColor[ev.status] ?? 'oklch(0.7 0 0)' }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-foreground/80 truncate">{ev.job_name}</div>
              <div className="text-[11px] text-muted-foreground">{ev.tenant_name}</div>
            </div>
            <div className="flex flex-col items-end gap-0.5 shrink-0">
              <span className="text-[10px] font-medium capitalize" style={{ color: statusColor[ev.status] ?? 'oklch(0.7 0 0)' }}>{ev.status.replace('_', ' ')}</span>
              <span className="text-[10px] text-muted-foreground">{ev.created_at}</span>
            </div>
          </div>
        ))}
      </Card>
    </div>
  )
}
